// Spec §4.3: UI logic hook for MultiEmailInputV2 component
import { useEffect } from 'react';
import type { MultiEmailInputV2Props } from './types';

/**
 * Stand-in input change handler used when the multi-email input receives no onInputChange prop. Performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

/**
 * Keeps the multi-email input's raw text in sync with the parent while working around the third-party control. Reports the typed value through onInputChange, and on each emailInputValue change reaches into the rendered #multi-emails-input-v2 input to write the value directly and attach a keyup listener, removing it on cleanup.
 *
 * @param props - Props carrying onInputChange and emailInputValue.
 * @returns The input change handler.
 */
export const useMultiEmailInputV2 = (props: MultiEmailInputV2Props) => {
  const { onInputChange = NOOP, emailInputValue = '' } = props;

  /**
   * Reports the email input's current text to the parent. Forwards the event target's value to the onInputChange callback.
   *
   * @param e - The input or keyup event carrying the current value.
   * @returns Nothing.
   */
  const onChangeInputValue = (e: any) => {
    onInputChange(e.target.value);
  };

  useEffect(() => {
    const elements = document.querySelector('#multi-emails-input-v2 .custom-email-input input');
    if (elements) {
      (elements as any).value = emailInputValue;
      elements.addEventListener('keyup', onChangeInputValue, false);
    }
    return () => {
      if (elements) {
        elements.removeEventListener('keyup', onChangeInputValue, false);
      }
    };
  }, [emailInputValue]);

  return { onChangeInputValue };
};
